import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
import { VisitContextRefSchema, VisitContextSectionKeySchema } from "./visit-context.js";
/**
 * Patient profile summary contracts: the short, physician-readable summary
 * that heads the profile page (owner requirement 2026-09-03: the profile's main
 * visual component is an AI summary of the patient).
 *
 * A summary is generated from one exact Patient Visit Context version
 * (api.get_visit_context, 20260831120100) and cached per (patient, context
 * version, prompt version) in clinical.patient_profile_summaries, so every
 * statement traces to an immutable chart snapshot and an unchanged chart never
 * pays for a second model call. Each line cites the context sections it was
 * built from; alerts are the chart's own allergies, safety flags and open
 * items. `origin` says whether a model wrote it or the deterministic builder
 * did, and `stale` says the chart moved since it was generated.
 */
export const PATIENT_PROFILE_SUMMARY_PROMPT_VERSION = "patient-profile-summary-v3";
export const PATIENT_SUMMARY_MAX_LINES = 6;
export const PATIENT_SUMMARY_MAX_ALERTS = 8;
export const PatientSummaryAlertKindSchema = z.enum(["allergy", "safety", "open_item"]);
export const PatientSummaryLineSchema = z
    .object({
    text: z.string().trim().min(1).max(280),
    /** Visit-context sections the line was built from; the card offers them as jumps. */
    sources: z.array(VisitContextSectionKeySchema).min(1).max(6),
})
    .strict();
export const PatientSummaryAlertSchema = z
    .object({
    kind: PatientSummaryAlertKindSchema,
    text: z.string().trim().min(1).max(160),
})
    .strict();
const summaryContentShape = {
    headline: z.string().trim().min(1).max(200),
    lines: z.array(PatientSummaryLineSchema).max(PATIENT_SUMMARY_MAX_LINES),
    alerts: z.array(PatientSummaryAlertSchema).max(PATIENT_SUMMARY_MAX_ALERTS),
};
/**
 * The generated content: what a model (or the deterministic builder) produces.
 * Strict so it doubles as the model's structured-output schema.
 */
export const PatientSummaryContentSchema = z.object(summaryContentShape).strict();
export const PatientSummaryOriginSchema = z.enum(["model", "deterministic"]);
const PromptVersionSchema = z.string().regex(/^[a-z][a-z0-9-]{2,79}$/);
const ModelIdSchema = z.string().min(1).max(120);
const ContextVersionSchema = z.coerce.number().int().positive();
/** Exact p_summary allow-list of api.record_patient_profile_summary. */
export const RecordPatientProfileSummaryInputSchema = z
    .object({
    ...summaryContentShape,
    contextVersion: z.number().int().positive(),
    contextContentHash: Sha256Schema,
    promptVersion: PromptVersionSchema,
    origin: PatientSummaryOriginSchema,
    model: ModelIdSchema.optional(),
})
    .strict();
/** One stored row as api.record_patient_profile_summary and api.get_patient_profile_summary return it. */
export const PatientProfileSummaryRecordSchema = z
    .object({
    ...summaryContentShape,
    id: IdSchema,
    patientId: IdSchema,
    contextVersion: ContextVersionSchema,
    contextContentHash: Sha256Schema,
    promptVersion: PromptVersionSchema,
    origin: PatientSummaryOriginSchema,
    model: ModelIdSchema.optional(),
    generatedAt: IsoDateTimeSchema,
    generatedBy: IdSchema,
})
    .passthrough();
/**
 * api.get_patient_profile_summary: the newest stored row (null when none) plus
 * the patient's latest assembled context version (null when the chart has
 * never been assembled), so a caller can tell a current summary from a stale
 * one without a second call.
 */
export const PatientProfileSummaryLookupSchema = z
    .object({
    summary: PatientProfileSummaryRecordSchema.nullable(),
    latestContextVersion: ContextVersionSchema.nullable(),
})
    .passthrough();
/**
 * What GET /v1/patients/:id/summary serves. `contextRef` and `assembledAt`
 * describe the chart version the page is looking at; `stale` means the
 * content predates it (a newer context exists and no fresh summary could be
 * produced), and `origin: "deterministic"` means the lines were built from the
 * record without a model.
 */
export const PatientProfileSummarySchema = z
    .object({
    ...summaryContentShape,
    origin: PatientSummaryOriginSchema,
    contextRef: VisitContextRefSchema,
    assembledAt: IsoDateTimeSchema,
    generatedAt: IsoDateTimeSchema,
    promptVersion: PromptVersionSchema,
    model: ModelIdSchema.optional(),
    stale: z.boolean(),
})
    .strict();
export const PatientProfileSummaryResponseSchema = z
    .object({ summary: PatientProfileSummarySchema })
    .strict();
//# sourceMappingURL=patient-summary.js.map